import React, { useMemo, useState } from 'react';
import { Star } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, YAxis } from 'recharts';
import { marketData, colors } from '../../utils/portfolioData';
type TabType = 'Favorites' | 'Top Traded' | 'Gainers' | 'Losers' | 'New';
function TrendChart({
  data,
  isPositive
}: {
  data: number[];
  isPositive: boolean;
}) {
  const chartData = data.map((val, i) => ({
    i,
    val
  }));
  const color = isPositive ? colors.success : colors.danger;
  const minVal = Math.min(...data);
  const maxVal = Math.max(...data);
  const range = maxVal - minVal;
  const padding = range * 0.1;
  return <div className="h-12 w-32" data-id="element-685">
      <ResponsiveContainer width="100%" height="100%" data-id="element-686">
        <AreaChart data={chartData} margin={{
        left: 0,
        right: 0
      }} data-id="element-687">
          <defs data-id="element-688">
            <linearGradient id={`gradient-${isPositive}`} x1="0" y1="0" x2="0" y2="1" data-id="element-689">
              <stop offset="5%" stopColor={color} stopOpacity={0.2} data-id="element-690" />
              <stop offset="95%" stopColor={color} stopOpacity={0} data-id="element-691" />
            </linearGradient>
          </defs>
          <YAxis hide domain={[minVal - padding, maxVal + padding]} type="number" data-id="element-692" />
          <Area type="monotone" dataKey="val" stroke={color} strokeWidth={1.5} fill={`url(#gradient-${isPositive})`} isAnimationActive={false} data-id="element-693" />
        </AreaChart>
      </ResponsiveContainer>
    </div>;
}
export function MarketSnapshot() {
  const [activeTab, setActiveTab] = useState<TabType>('Favorites');
  const tabs: TabType[] = ['Favorites', 'Top Traded', 'Gainers', 'Losers', 'New'];
  // Filter and sort markets based on active tab
  const filteredMarkets = useMemo(() => {
    let filtered = [...marketData];
    switch (activeTab) {
      case 'Favorites':
        // Show only favorited markets
        filtered = filtered.filter(m => m.isFavorite);
        break;
      case 'Top Traded':
        // Sort by volume (highest first)
        filtered = filtered.sort((a, b) => {
          const volumeA = parseFloat(a.volume24h.replace('M', ''));
          const volumeB = parseFloat(b.volume24h.replace('M', ''));
          return volumeB - volumeA;
        });
        break;
      case 'Gainers':
        // Show only positive changes, sorted by highest gain
        filtered = filtered.filter(m => m.change24h > 0).sort((a, b) => b.change24h - a.change24h);
        break;
      case 'Losers':
        // Show only negative changes, sorted by biggest loss
        filtered = filtered.filter(m => m.change24h < 0).sort((a, b) => a.change24h - b.change24h);
        break;
      case 'New':
        // Show newer/smaller cap coins (LINK, MATIC, AVAX, ADA, DOT, XRP)
        const newCoins = ['LINK/USD', 'MATIC/USD', 'AVAX/USD', 'ADA/USD', 'DOT/USD', 'XRP/USD'];
        filtered = filtered.filter(m => newCoins.includes(m.symbol));
        break;
      default:
        break;
    }
    return filtered;
  }, [activeTab]);
  return <div className="glass-border rounded-[20px] overflow-hidden h-full flex flex-col" data-id="element-694">
      {/* Header with Tabs */}
      <div className="border-b border-border-subtle flex-shrink-0" data-id="element-695">
        <div className="flex items-center justify-between px-6 py-4" data-id="element-696">
          <div className="flex items-center gap-1" data-id="element-697">
            {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-3 py-1.5 text-sm font-ui rounded-lg transition-all ${activeTab === tab ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-698">
                {tab === 'Favorites' && <Star size={14} className="inline mr-1.5" data-id="element-699" />}
                {tab}
              </button>)}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto flex-1 min-h-0" data-id="element-700">
        <table className="w-full" data-id="element-701">
          <thead className="bg-background-elevated/50 sticky top-0" data-id="element-702">
            <tr className="border-b border-border-subtle" data-id="element-703">
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-704">
                Market
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-705">
                Price
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-706">
                24H Low
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-707">
                24H High
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-708">
                24H Chg.
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-709">
                24H Trend
              </th>
            </tr>
          </thead>
          <tbody data-id="element-710">
            {filteredMarkets.length > 0 ? filteredMarkets.map(market => <tr key={market.symbol} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors group" data-id="element-711">
                  {/* Market */}
                  <td className="px-6 py-4" data-id="element-712">
                    <div className="flex items-center gap-3" data-id="element-713">
                      <img src={market.logo} alt={market.name} className="w-6 h-6 object-contain" data-id="element-714" />
                      <div data-id="element-715">
                        <div className="text-sm font-medium text-text-primary" data-id="element-716">
                          {market.symbol}
                        </div>
                        <div className="text-xs text-text-secondary" data-id="element-717">
                          {market.leverage}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Price */}
                  <td className="px-6 py-4 text-right" data-id="element-718">
                    <div className="font-numbers text-sm text-text-primary" data-id="element-719">
                      {market.price.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                      <span className="text-text-secondary text-xs ml-1" data-id="element-720">
                        USD
                      </span>
                    </div>
                  </td>

                  {/* 24H Low */}
                  <td className="px-6 py-4 text-right" data-id="element-721">
                    <div className="font-numbers text-sm text-text-primary" data-id="element-722">
                      {market.low24h.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                      <span className="text-text-secondary text-xs ml-1" data-id="element-723">
                        USD
                      </span>
                    </div>
                  </td>

                  {/* 24H High */}
                  <td className="px-6 py-4 text-right" data-id="element-724">
                    <div className="font-numbers text-sm text-text-primary" data-id="element-725">
                      {market.high24h.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                      <span className="text-text-secondary text-xs ml-1" data-id="element-726">
                        USD
                      </span>
                    </div>
                  </td>

                  {/* 24H Change */}
                  <td className="px-6 py-4 text-right" data-id="element-727">
                    <div className={`font-numbers text-sm ${market.change24h >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-728">
                      {market.change24h >= 0 ? '+' : ''}
                      {market.change24h.toFixed(2)}%
                    </div>
                  </td>

                  {/* 24H Trend */}
                  <td className="px-6 py-4" data-id="element-729">
                    <div className="flex justify-end" data-id="element-730">
                      <TrendChart data={market.priceHistory} isPositive={market.change24h >= 0} data-id="element-731" />
                    </div>
                  </td>
                </tr>) : <tr data-id="element-732">
                <td colSpan={6} className="px-6 py-12 text-center" data-id="element-733">
                  <div className="text-text-secondary text-sm" data-id="element-734">
                    No markets found for {activeTab}
                  </div>
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </div>;
}